// SelectInput.jsx

import React, { forwardRef } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SelectInput.css';

/**
 * SelectInput
 *
 * Renders a themed select input with optional leading and trailing icons,
 * helper text, and error styling.
 *
 * @param {string} value - Current selected value.
 * @param {function} onChange - Callback when selection changes.
 * @param {ReactNode} startIcon - Optional leading icon component.
 * @param {string} startIconColor - Optional leading icon color override.
 * @param {ReactNode} endIcon - Optional trailing icon component.
 * @param {string} endIconColor - Optional trailing icon color override.
 * @param {number} iconSize - Icon size in pixels.
 * @param {boolean} error - Whether error styling is active.
 * @param {string|number} fontSize - Select font size value.
 * @param {string} helperText - Optional helper or error message text.
 * @param {boolean} disabled - Whether the select is disabled.
 * @param {object} style - Inline style overrides for the select.
 * @param {string} className - Optional additional class names.
 * @param {boolean} hybrid - Whether to use hybrid transparent background mode.
 * @param {ReactNode} children - Option elements rendered inside the select.
 * @param {object} rest - Additional native select props.
 *
 * @returns {ReactNode} The rendered component.
 */
const SelectInput = forwardRef(({
    value,
    onChange,
    startIcon: StartIcon,
    startIconColor,
    endIcon: EndIcon,
    endIconColor,
    iconSize = 20,
    error = false,
    fontSize = '14px',
    helperText = '',
    disabled = false,
    style = {},
    className = '',
    hybrid = false,
    children,
    ...rest
}, ref) => {
    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // ----- UTILITIES ----- //

    const effectiveStartIconColor = startIconColor ?? theme.foregroundMuted;
    const effectiveEndIconColor = endIconColor ?? theme.foregroundMuted;
    const selectColorScheme = theme.key === 'LIGHT' ? 'light' : 'dark';
    const hybridSafeBackground = theme.key === 'LIGHT'
        ? 'rgba(255, 255, 255, 0.001)'
        : 'rgba(0, 0, 0, 0.001)';

    const baseClass = `select-input-container${error ? ' select-input-error' : ''}${hybrid ? ' select-input-hybrid' : ''} ${className}`.trim();
    const wrapperClass = `select-input-wrapper${disabled ? ' disabled' : ''}`;

    const dynamicPadding = {
        paddingLeft: StartIcon ? 40 : 14,
        paddingRight: EndIcon ? 40 : 14,
    };

    // ----- RENDER ----- //

    return (
        <div className={wrapperClass}>
            <div className={`select-input-inner${StartIcon ? ' has-start' : ''}${EndIcon ? ' has-end' : ''}`}>
                {StartIcon && (
                    <div
                        className="select-input-icon start"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        <StartIcon
                            color={effectiveStartIconColor}
                            height={iconSize}
                            width={iconSize}
                        />
                    </div>
                )}

                <select
                    className={baseClass}
                    style={{
                        '--select-input-bg': hybrid ? hybridSafeBackground : theme.input,
                        '--select-input-hover-bg': theme.input,
                        '--select-input-option-bg': theme.background,
                        '--select-input-option-fg': theme.foreground,
                        ...dynamicPadding,
                        border: error ? `1px solid ${theme.red}` : undefined,
                        color: theme.foreground,
                        colorScheme: selectColorScheme,
                        fontSize,
                        ...style,
                    }}
                    disabled={disabled}
                    ref={ref}
                    value={value}
                    {...rest}
                    onChange={onChange}
                >
                    {children}
                </select>

                {EndIcon && (
                    <div
                        className="select-input-icon end"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        <EndIcon
                            color={effectiveEndIconColor}
                            height={iconSize}
                            width={iconSize}
                        />
                    </div>
                )}
            </div>

            {helperText && (
                <div
                    className={`select-input-helper${error ? ' error' : ''}`}
                    style={{
                        color: error ? theme.red : theme.foregroundMuted,
                    }}
                >
                    {helperText}
                </div>
            )}
        </div>
    );
});

// Dev Tools
SelectInput.displayName = 'SelectInput';

export default SelectInput;
